import type { Palette, ThemeName } from '../types';

/** Design tokens — Mercado design system (color values synced with SCSS themes).
 * Accent palette is Cuban-inspired: sugarcane/palm green, rum & tobacco ambers,
 * warm sand/cream base — deliberately earthy, no fluorescent tones. */
export const lightPalette = (): Palette => ({
  bg: '#f1ebd8',
  bg2: '#e6dec2',
  paper: '#faf5e6',
  surface: '#faf5e6',
  line: '#d4cdb8',
  line2: '#a89d80',
  ink: '#0d0d0c',
  muted: '#6b6553',
  teal: '#3f6b34',
  tealLight: '#5c8a3f',
  terra: '#b5561d',
  terraLight: '#d97328',
  sand: '#e6dec2',
  success: '#3f7d3a',
  warn: '#b8801a',
  danger: '#c0392b',
});

export const darkPalette = (): Palette => ({
  bg: '#0f0f0d',
  bg2: '#1a1a17',
  paper: '#1c1c19',
  surface: '#1c1c19',
  line: '#2a2924',
  line2: '#3d3b34',
  ink: '#f1ebd8',
  muted: '#8a8270',
  teal: '#9cbf78',
  tealLight: '#b6d493',
  terra: '#e08a4a',
  terraLight: '#f0a86a',
  sand: '#2a2924',
  success: '#84b46a',
  warn: '#e0b24a',
  danger: '#f87171',
});

export function resolveThemeName(theme: ThemeName): 'light' | 'dark' {
  const prefersDark =
    typeof window !== 'undefined' &&
    window.matchMedia?.('(prefers-color-scheme: dark)').matches;

  if (theme === 'dark' || (theme === 'system' && prefersDark)) {
    return 'dark';
  }
  return 'light';
}

export function applyTheme(theme: ThemeName): void {
  if (typeof document === 'undefined') return;
  document.documentElement.dataset.theme = resolveThemeName(theme);
}

export function resolvePalette(theme: ThemeName): Palette {
  return resolveThemeName(theme) === 'dark' ? darkPalette() : lightPalette();
}

export const tokens = {
  fontDisplay: "'Manrope', system-ui, sans-serif",
  fontSerif: "'Source Serif 4', 'Iowan Old Style', 'Palatino Linotype', Georgia, serif",
  fontMono: "'JetBrains Mono', monospace",
  radiusCard: '0px',
  radiusInput: '0px',
  radiusPill: '999px',
  radiusChip: '0px',
  borderWidth: '1px',
  spacingXs: '6px',
  spacingSm: '12px',
  spacingMd: '20px',
  spacingLg: '32px',
  colorOnAccent: 'var(--color-on-accent)',
} as const;

export type { Palette, ThemeName };
